import { Icon } from "@iconify/react";
import Div from "../Div";
import SectionHeading from "../SectionHeading";
import Spacing from "../Spacing";
import Cta from "../Cta";
import Button from "../Button";
import IconBox from "../IconBox";
import "./salesService.scss";

const heroStats = [
  { icon: "mdi:account-tie-voice", label: "Dedicated Agents" },
  { icon: "mdi:message-processing", label: "Multi-Channel Support" },
  { icon: "mdi:chart-timeline-variant", label: "Scalable Teams" },
];

const salesAgentServices = [
  {
    title: "Lead Generation",
    description:
      "Identify and engage potential customers who fit your ideal client profile.",
    icon: "mdi:account-search",
  },
  {
    title: "Appointment Setting",
    description:
      "Schedule qualified meetings directly into your calendar, allowing your team to focus on closing deals.",
    icon: "mdi:calendar-check",
  },
  {
    title: "Outbound Sales",
    description:
      "Professional outreach through phone, email, LinkedIn, and other channels to create new business opportunities.",
    icon: "mdi:phone-outgoing",
  },
  {
    title: "Follow-Up Management",
    description:
      "Ensure no prospect falls through the cracks with structured follow-up campaigns.",
    icon: "mdi:email-sync",
  },
  {
    title: "CRM Management",
    description:
      "Maintain organized customer records, lead tracking, and sales pipeline management.",
    icon: "mdi:database",
  },
  {
    title: "Sales Support",
    description:
      "Assist your internal sales team with prospecting, research, and customer engagement activities.",
    icon: "mdi:handshake",
  },
];

const customerServiceItems = [
  {
    title: "Live Customer Support",
    description: "Handle customer inquiries promptly and professionally.",
    icon: "mdi:headset",
  },
  {
    title: "Email Support",
    description:
      "Manage customer communications with accuracy and efficiency.",
    icon: "mdi:email-outline",
  },
  {
    title: "Chat Support",
    description:
      "Provide real-time assistance through website and social media chat channels.",
    icon: "mdi:chat-processing",
  },
  {
    title: "Order Processing Assistance",
    description: "Support customers throughout the purchasing journey.",
    icon: "mdi:cart-check",
  },
  {
    title: "Customer Retention Support",
    description:
      "Build stronger customer relationships and improve satisfaction rates.",
    icon: "mdi:heart-pulse",
  },
  {
    title: "Complaint Resolution",
    description:
      "Handle customer concerns professionally while protecting your brand reputation.",
    icon: "mdi:shield-check",
  },
  {
    title: "Administrative Assistance",
    description:
      "Support routine customer service and back-office functions.",
    icon: "mdi:clipboard-list",
  },
];

const whyChooseCards = [
  {
    title: "Skilled Professionals",
    subtitle:
      "Our agents are selected based on communication skills, professionalism, and adaptability.",
    icon: "/images/icons/service_icon_1.svg",
  },
  {
    title: "Flexible Solutions",
    subtitle: "Scale your team up or down according to your business needs.",
    icon: "/images/icons/service_icon_2.svg",
  },
  {
    title: "Cost-Effective Staffing",
    subtitle:
      "Reduce hiring and operational costs while maintaining service quality.",
    icon: "/images/icons/service_icon_3.svg",
  },
  {
    title: "Dedicated Support",
    subtitle:
      "Receive professionals focused on your business objectives and customer experience.",
    icon: "/images/icons/service_icon_1.svg",
  },
  {
    title: "Industry Versatility",
    subtitle:
      "We support startups, e-commerce businesses, agencies, consultants, healthcare providers, educational institutions, and more.",
    icon: "/images/icons/service_icon_2.svg",
  },
];

const idealClients = [
  { label: "Startups", icon: "mdi:rocket-launch" },
  { label: "E-Commerce Businesses", icon: "mdi:shopping" },
  { label: "Energy Saving and Solar Companies", icon: "mdi:solar-power" },
  { label: "Real Estate Companies", icon: "mdi:home-city" },
  { label: "SaaS & Technology Firms", icon: "mdi:laptop" },
  { label: "Marketing Agencies", icon: "mdi:bullhorn" },
  { label: "Healthcare Providers", icon: "mdi:hospital-box" },
  { label: "Educational Institutions", icon: "mdi:school" },
  { label: "Professional Service Firms", icon: "mdi:briefcase" },
  { label: "Consultants & Coaches", icon: "mdi:account-supervisor" },
];

function ServiceFeatureCard({ title, description, icon }) {
  return (
    <Div className="col-lg-6">
      <Div className="cs-sales_feature">
        <Div className="cs-sales_feature_icon">
          <Icon icon={icon} />
        </Div>
        <h4 className="cs-semi_bold">{title}</h4>
        <p>{description}</p>
      </Div>
      <Spacing lg="24" md="24" />
    </Div>
  );
}

export default function SalesAgentsCustomerService() {
  return (
    <>
      <Div className="container">
        <Div className="row align-items-center">
          <Div className="col-xl-5 col-lg-6">
            <SectionHeading
              title="Sales Agents & Customer Service Providers"
              subtitle="Our Service"
            >
              <Spacing lg="30" md="20" />
              <h3 className="cs-font_30 cs-m0 cs-semi_bold cs-line_height_4">
                Build Your Business While We Handle the Conversations
              </h3>
              <Spacing lg="20" md="15" />
              <p className="cs-m0">
                Professional Sales Agents and Customer Service Representatives
                dedicated to helping your business grow, convert more leads,
                and deliver exceptional customer experiences.
              </p>
              <Spacing lg="20" md="15" />
              <p className="cs-m0">
                Whether you need outbound sales professionals, inbound support
                representatives, appointment setters, lead generation
                specialists, or customer service teams, Acsy Tech provides
                trained professionals who become an extension of your business.
              </p>
              <Spacing lg="30" md="25" />
              <Div className="d-flex flex-wrap" style={{ gap: "20px" }}>
                <Button btnText="Hire a Dedicated Agent" btnLink="/contact" />
                <Button
                  btnText="Request a Free Consultation"
                  btnLink="/appointment"
                />
              </Div>
              <Spacing lg="30" md="30" />
              <Div className="cs-separator cs-accent_bg"></Div>
            </SectionHeading>
          </Div>
          <Div className="col-lg-6 col-xl-5 offset-xl-1">
            <img
              src="/images/service_3.jpg"
              alt="Sales Agents and Customer Service"
              className="w-100 cs-radius_15"
            />
            <Spacing lg="30" md="30" />
            <Div className="row">
              {heroStats.map((stat) => (
                <Div className="col-sm-4" key={stat.label}>
                  <Div className="cs-sales_hero_stat">
                    <Icon
                      icon={stat.icon}
                      className="cs-sales_hero_stat_icon"
                    />
                    <h4 className="cs-semi_bold">{stat.label}</h4>
                  </Div>
                  <Spacing lg="0" md="20" />
                </Div>
              ))}
            </Div>
          </Div>
        </Div>
      </Div>

      <Spacing lg="150" md="80" />
      <Div className="container">
          <Div className="cs-sales_section_intro">
            <SectionHeading
              title="Professional Sales Teams That Drive Revenue"
              subtitle="Sales Agent Services"
            >
              <Spacing lg="30" md="20" />
              <p className="cs-m0">
                Growing a business requires consistent outreach, relationship
                building, and effective follow-up. Our sales professionals help
                businesses generate qualified leads, nurture prospects, and close
                more opportunities.
              </p>
              <Spacing lg="25" md="25" />
              <Div className="cs-separator cs-accent_bg"></Div>
            </SectionHeading>
          </Div>
          <Spacing lg="50" md="40" />
          <Div className="row">
            {salesAgentServices.map((item) => (
              <ServiceFeatureCard key={item.title} {...item} />
            ))}
          </Div>
        </Div>

      <Spacing lg="150" md="80" />
      <Div className="container">
        <Div className="cs-sales_section_intro">
          <SectionHeading
            title="Deliver Exceptional Customer Experiences"
            subtitle="Customer Service Providers"
          >
            <Spacing lg="30" md="20" />
            <p className="cs-m0">
              Customer satisfaction is the foundation of every successful
              business. Our customer support specialists help businesses provide
              responsive, professional, and customer-focused service across
              multiple channels.
            </p>
            <Spacing lg="25" md="25" />
            <Div className="cs-separator cs-accent_bg"></Div>
          </SectionHeading>
        </Div>
        <Spacing lg="50" md="40" />
        <Div className="row">
          {customerServiceItems.map((item) => (
            <ServiceFeatureCard key={item.title} {...item} />
          ))}
        </Div>
      </Div>

      <Spacing lg="150" md="80" />
      <Div className="container">
          <SectionHeading
            title="Why Choose Acsy Tech?"
            subtitle="Our Advantage"
          />
          <Spacing lg="70" md="40" />
          <Div className="row cs-sales_why_choose justify-content-center">
            {whyChooseCards.map((card) => (
              <Div className="col-lg-4 col-md-6" key={card.title}>
                <IconBox
                  title={card.title}
                  subtitle={card.subtitle}
                  icon={card.icon}
                />
                <Spacing lg="30" md="30" />
              </Div>
            ))}
          </Div>
      </Div>

      <Spacing lg="150" md="80" />
      <Div className="container">
        <Div className="row align-items-start">
          <Div className="col-lg-5">
            <SectionHeading title="Who We Work With" subtitle="Ideal Clients">
              <Spacing lg="30" md="20" />
              <p className="cs-m0">
                We partner with businesses across industries that need reliable
                sales outreach and customer support — from fast-growing startups
                to established enterprises.
              </p>
              <Spacing lg="25" md="25" />
              <Div className="cs-separator cs-accent_bg"></Div>
            </SectionHeading>
            <Spacing lg="0" md="40" />
          </Div>
          <Div className="col-lg-6 offset-lg-1">
            <Div className="cs-sales_clients_grid">
              {idealClients.map((client) => (
                <Div className="cs-sales_client_item" key={client.label}>
                  <Div className="cs-sales_client_icon">
                    <Icon icon={client.icon} />
                  </Div>
                  <span className="cs-semi_bold">{client.label}</span>
                </Div>
              ))}
            </Div>
          </Div>
        </Div>
      </Div>

      <Spacing lg="150" md="80" />
      <Div className="container">
        <Div className="text-center cs-sales_section_intro mx-auto">
          <SectionHeading
            title="Let Your Team Focus on Growth"
            subtitle="Outsource With Confidence"
          >
            <Spacing lg="30" md="20" />
            <p className="cs-m0">
              By outsourcing sales and customer service functions to Acsy Tech,
              businesses can increase lead generation efforts, improve customer
              satisfaction, reduce operational overhead, expand support
              capacity, improve response times, and focus internal resources on
              core business activities.
            </p>
            <Spacing lg="25" md="25" />
            <p className="cs-m0">
              Ready to grow your sales and support team? Contact Acsy Tech
              today.
            </p>
          </SectionHeading>
        </Div>
        <Spacing lg="50" md="40" />
        <Cta
          title="Ready to grow your sales <br />and support team?"
          btnText="Contact Acsy Tech"
          btnLink="/contact"
          bgSrc="/images/cta_bg.jpeg"
        />
      </Div>
    </>
  );
}
